import { css } from "@emotion/css";
import type { CalloutType } from "@octopusdeploy/design-system-components";
import { Tooltip, Callout } from "@octopusdeploy/design-system-components";
import type { ArgoCDGatewayResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import React, { useCallback, useMemo } from "react";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import Note from "~/primitiveComponents/form/Note/Note";
import DateFormatter from "~/utils/DateFormatter/index";
const styles = {
    link: css({
        cursor: "pointer",
    }),
};
type ArgoCDGatewayHealthCalloutProps = {
    gateway: ArgoCDGatewayResource;
};
export const ArgoCDGatewayHealthCallout = (props: ArgoCDGatewayHealthCalloutProps) => {
    const { gateway } = props;
    const { navigate } = useSpaceAwareNavigation();
    const createServerTaskLink = useCallback((label: string, taskId: string) => {
        const serverTaskLink = links.taskPage.generateUrl({ taskId });
        return (<Tooltip content={"View task"}>
                    <a className={styles.link} onClick={() => navigate(serverTaskLink)}>
                        {label}
                    </a>
                </Tooltip>);
    }, [navigate]);
    const [type, title, description]: [
        CalloutType,
        string,
        string | React.ReactElement
    ] = useMemo(() => {
        const serverTaskSuffix = gateway.HealthCheckServerTaskId ? <span> Check the {createServerTaskLink("health check task", gateway.HealthCheckServerTaskId)} for more information</span> : undefined;
        switch (gateway.HealthStatus) {
            case "Unknown":
                return ["information", "Unknown", "This Argo CD instance was just added, and a health check has not been performed."];
            case "Healthy":
                return ["success", "Healthy", "The gateway is healthy and is communicating with Argo CD successfully."];
            case "ArgoCDConnectionIssues":
                return ["warning", "Argo CD Connectivity Issues", <span>The gateway is running, however there are problems communicating with the target Argo CD instance.{serverTaskSuffix}</span>];
            case "Unavailable":
                return ["danger", "Unavailable", <span>Octopus Server cannot connect to the gateway.{serverTaskSuffix}</span>];
            default:
                exhaustiveCheck(gateway.HealthStatus, "Unknown health status type");
        }
    }, [createServerTaskLink, gateway.HealthCheckServerTaskId, gateway.HealthStatus]);
    const formattedLastChecked = useMemo(() => (gateway.HealthLastChecked ? DateFormatter.momentAgo(gateway.HealthLastChecked) : "Never"), [gateway.HealthLastChecked]);
    const serverTaskLink = useMemo(() => (gateway.HealthCheckServerTaskId ? createServerTaskLink(formattedLastChecked, gateway.HealthCheckServerTaskId) : formattedLastChecked), [createServerTaskLink, formattedLastChecked, gateway.HealthCheckServerTaskId]);
    return (<Callout type={type} title={title}>
            <p>{description}</p>
            <Note>Last health check: {serverTaskLink}</Note>
        </Callout>);
};
